import {
  ListNext as List,
  ListHeader,
  ListItemBaseSection,
  LoadingSpinner,
} from "@momentum-ui/react-collaboration";
import { IWebexIntContact } from "@webex/component-adapter-interfaces/dist/esm/src";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SearchContactsItem } from "../components/SearchContacts/SearchContactsItem";
import { SearchContactsListEmpty } from "../components/SearchContacts/SearchContactsListEmpty";
import { usePresenceContext } from "../contexts/PresenceContext";
import { useContactSearch } from "../hooks/useContactSearch";
import { useMakeCall } from "../hooks/useMakeCall";
import { usePresence } from "../hooks/usePresence";
import useWebexClasses from "../hooks/useWebexClasses";
import { requestParticipantAvatarUrl } from "../services/avatarService";
import { THEME_CLASS, WEBEX_CALLING_THEME } from "../utils/ThemeUtils";
import { resolveWebexUserId } from "../utils/webexUserIdMapper";
import "./WebexSearchContactsList.styles.scss";

type SearchContactsListProps = {
  searchValue: string;
  noContactsFoundMessage: string;
  onUserSelect?: (user: IWebexIntContact | undefined) => void;
  hideSource?: boolean;
  style?: React.CSSProperties;
  label?: string;
  theme?: string;
  clearSearchInput: () => void;
  makeCall?: (address: string, isVideo?: boolean, label?: string) => void;
};

/**
 *
 * @param root0
 * @param root0.style
 * @param root0.searchValue
 * @param root0.noContactsFoundMessage
 * @param root0.onSelectUser
 * @param root0.onUserSelect
 * @param root0.hideSource
 */
export const WebexSearchContactsList = ({
  searchValue,
  noContactsFoundMessage,
  onUserSelect = () => { },
  hideSource = false,
  style = undefined,
  label,
  theme,
  clearSearchInput,
  makeCall: makeCallProp,
}: SearchContactsListProps) => {
  let globalIndex = 0;
  const [sources, filteredContactResponse, isSearching] =
    useContactSearch(searchValue);
  const [selectedUserId, setSelectedUserId] = useState<string>();
  const [cssClasses, sc] = useWebexClasses("search-contacts-list", undefined, {
    loading: isSearching,
  });
  const { t } = useTranslation();

  // Presence: resolve Webex user IDs and register for search result users
  const { getPresence, registerUsers } = usePresence('searchContacts');
  const { manager: presenceManager } = usePresenceContext();
  const [contactIdToWebexUserId, setContactIdToWebexUserId] = useState<Map<string, string>>(new Map());
  const resolveDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Flatten all users from all sources (Directory, Outlook, etc.)
  const allSearchUsers = useMemo(() => {
    const users: IWebexIntContact[] = [];
    sources.forEach((source) => {
      const items = filteredContactResponse.items[source] || [];
      users.push(...items);
    });
    return users;
  }, [sources, filteredContactResponse.items]);

  // Stable key for the resolve effect: only changes when the actual set of contact IDs changes.
  // Using this instead of [allSearchUsers] avoids re-running the effect when sources/getSources()
  // returns a new array reference each render while the result set is unchanged.
  const allSearchUsersKey = useMemo(
    () =>
      searchValue +
      '|' +
      [...allSearchUsers]
        .map((u) => u.id)
        .sort()
        .join(','),
    [searchValue, allSearchUsers]
  );

  // Resolve Webex user IDs with debounce (avoid running on every keystroke and blocking UI).
  // Resolve *all* search rows (previously capped at 25, so scrolled rows never got presence).
  // Chunk parallel lookups to avoid hundreds of simultaneous People API calls.
  const PRESENCE_RESOLVE_DEBOUNCE_MS = 400;
  const PRESENCE_RESOLVE_CHUNK = 40;
  const resolveMountedRef = useRef(true);
  const resolveGenerationRef = useRef(0);

  useEffect(() => {
    const resolveGeneration = ++resolveGenerationRef.current;

    if (!allSearchUsers.length) {
      // Only update when we actually had entries; avoid setState when already empty
      // to prevent re-render loop (allSearchUsers can get a new ref each render via sources).
      setContactIdToWebexUserId((prev) => (prev.size === 0 ? prev : new Map()));
      return;
    }

    if (resolveDebounceRef.current) {
      clearTimeout(resolveDebounceRef.current);
      resolveDebounceRef.current = null;
    }

    resolveMountedRef.current = true;
    resolveDebounceRef.current = setTimeout(() => {
      resolveDebounceRef.current = null;
      const usersToResolve = allSearchUsers;

      void (async () => {
        const merged = new Map<string, string>();
        for (let i = 0; i < usersToResolve.length; i += PRESENCE_RESOLVE_CHUNK) {
          if (
            !resolveMountedRef.current ||
            resolveGeneration !== resolveGenerationRef.current
          ) {
            return;
          }
          const chunk = usersToResolve.slice(i, i + PRESENCE_RESOLVE_CHUNK);
          const pairs = await Promise.all(
            chunk.map(async (user) => {
              const email = user.emailAddresses?.[0]?.address;
              const pn0 = user.phoneNumbers?.[0] as
                | { address?: string; value?: string }
                | undefined;
              const phone = pn0?.address ?? pn0?.value;
              const resolved = await resolveWebexUserId({
                contactId: user.id,
                displayName: user.name,
                email,
                phone,
              });
              return resolved ? ([user.id, resolved] as const) : null;
            })
          );
          pairs.forEach((p) => {
            if (p) merged.set(p[0], p[1]);
          });
          if (
            resolveMountedRef.current &&
            resolveGeneration === resolveGenerationRef.current
          ) {
            setContactIdToWebexUserId(new Map(merged));
          }
        }
      })();
    }, PRESENCE_RESOLVE_DEBOUNCE_MS);

    return () => {
      resolveMountedRef.current = false;
      if (resolveDebounceRef.current) {
        clearTimeout(resolveDebounceRef.current);
        resolveDebounceRef.current = null;
      }
    };
  }, [allSearchUsersKey]);

  // Register resolved Webex user IDs with PresenceManager.
  // Use stable serialized key so this array reference only changes when IDs actually change,
  // preventing the effect from firing on every re-render (e.g. from presence context updates).
  const webexUserIdsKey = useMemo(() => {
    const ids = Array.from(contactIdToWebexUserId.values()).filter(Boolean).sort();
    return ids.join(',');
  }, [contactIdToWebexUserId]);
  const webexUserIds = useMemo(
    () => Array.from(contactIdToWebexUserId.values()).filter(Boolean),
    [webexUserIdsKey, contactIdToWebexUserId]
  );

  useEffect(() => {
    if (!presenceManager) {
      return;
    }
    if (webexUserIds.length > 0) {
      registerUsers(webexUserIds, { force: true });
    } else {
      registerUsers([]);
    }
  }, [presenceManager, webexUserIdsKey, webexUserIds, registerUsers]);

  const sourceTranslationKeys: Record<string, string> = {
    "Directory contacts": "label.contact_search.directory_contacts",
    "Outlook contacts": "label.contact_search.outlook_contacts",
    "Devices": "label.contact_search.devices",
  };

  const setSeletedUser = useCallback(
    (user?: IWebexIntContact) => {
      onUserSelect(user);
      setSelectedUserId(user?.id);
    },
    [onUserSelect, setSelectedUserId]
  );

  const [adapterMakeCall] = useMakeCall();
  const makeCall = makeCallProp ?? adapterMakeCall;

  useEffect(() => {
    setSeletedUser(undefined);
  }, [searchValue]);

  if (!isSearching && filteredContactResponse.count === 0) {
    return (
      <SearchContactsListEmpty
        noContactsFoundMessage={noContactsFoundMessage}
      />
    );
  }

  return (
    <List
      shouldFocusOnPress
      listSize={filteredContactResponse.count}
      className={`${cssClasses} ${theme === WEBEX_CALLING_THEME.DARK
        ? THEME_CLASS.DARK
        : theme === WEBEX_CALLING_THEME.LIGHT
          ? THEME_CLASS.LIGHT
          : ""
        }`}
      style={style}
    >
      {sources
        .filter(
          (source) =>
            isSearching ||
            (filteredContactResponse.items[source] || []).length > 0
        )
        .map((source) => (
          <React.Fragment key={source}>
            {!hideSource && (
              <ListHeader>
                <ListItemBaseSection
                  position="fill"
                  className={sc("source-header")}
                >
                  {sourceTranslationKeys[source] ? t(sourceTranslationKeys[source]) : source}
                </ListItemBaseSection>
              </ListHeader>
            )}
            {isSearching ? (
              <LoadingSpinner
                scale={32}
                className={sc("loading")}
                data-testid={`loading-spinner-${source}`}
              />
            ) : (
              filteredContactResponse.items[source].map((user) => {
                const currentIndex = globalIndex; // Assign the current index to a local variable
                globalIndex++; // Increment the global index for the next iteration
                const webexUserId = contactIdToWebexUserId.get(user.id);
                const presence = webexUserId ? getPresence(webexUserId) : undefined;

                const userForRow: IWebexIntContact = webexUserId
                  ? {
                      ...user,
                      fetchAvatarUrl: () =>
                        requestParticipantAvatarUrl(webexUserId, { size: 40 }),
                    }
                  : user;

                return (
                  <SearchContactsItem
                    clearSearchInput={clearSearchInput}
                    key={user.id}
                    user={userForRow}
                    index={currentIndex}
                    isSelected={selectedUserId === user.id}
                    onPress={() => setSeletedUser(user)}
                    makeCall={makeCall}
                    label={label}
                    presenceType={presence?.presenceType}
                    presenceLabel={presence?.presenceLabel}
                  />
                );
              })
            )}
          </React.Fragment>
        ))}
    </List>
  );
};
